import type { EChartsOption } from "echarts";
import { Card, DonutChart, EChart, StackedBars, TrendLine, chartPalette } from "@ncp/ui";
import type { ChartSpec } from "./api";

function BarChart({ spec }: { spec: ChartSpec }) {
  const option: EChartsOption = {
    tooltip: { trigger: "axis", valueFormatter: (v) => `${v}${spec.unit ? ` ${spec.unit}` : ""}` },
    legend: spec.series.length > 1 ? { bottom: 0, textStyle: { fontSize: 11 } } : undefined,
    grid: { left: 12, right: 20, top: 12, bottom: spec.series.length > 1 ? 36 : 12, containLabel: true },
    xAxis: { type: "value", splitLine: { lineStyle: { color: "#f0ebe1" } }, axisLabel: { formatter: (v: number) => `${v}${spec.unit === "%" ? "%" : ""}` } },
    yAxis: { type: "category", data: spec.labels, inverse: true, axisLine: { show: false }, axisTick: { show: false }, axisLabel: { width: 160, overflow: "truncate", fontSize: 11 } },
    series: spec.series.map((s, i) => ({
      name: s.name,
      type: "bar",
      barMaxWidth: 22,
      data: s.values.map((v, j) => ({ value: v, itemStyle: { color: spec.series.length > 1 ? chartPalette[i % chartPalette.length] : chartPalette[j % chartPalette.length], borderRadius: [0, 6, 6, 0] } })),
      label: { show: true, position: "right", fontSize: 11, formatter: (p) => `${p.value}${spec.unit === "%" ? "%" : ""}` },
    })),
  };
  return <EChart option={option} height={Math.max(160, 28 * spec.labels.length + 40)} />;
}

/** Renders the chart specifications produced by the Market Study Agent (donut / bar / line) with the Sand palette. */
export function StudyChart({ spec }: { spec: ChartSpec }) {
  switch (spec.kind) {
    case "donut": {
      const data = spec.labels.map((name, i) => ({ name, value: spec.series[0]?.values[i] ?? 0 }));
      const firms = data.filter((d) => !/^(others?|imports?|أخرى|الواردات)$/i.test(d.name.trim()));
      const cr4 = firms.slice(0, 4).reduce((a, b) => a + b.value, 0);
      return <DonutChart data={data} height={260} center={spec.unit === "%" ? `CR4\n${Math.round(cr4)}%` : undefined} />;
    }
    case "bar":
      if (spec.series.length > 1 && spec.labels.every((l) => /^\d{4}$/.test(l.trim()))) {
        return <StackedBars categories={spec.labels} series={spec.series.map((s) => ({ name: s.name, data: s.values }))} height={280} />;
      }
      return <BarChart spec={spec} />;
    case "line":
      return <TrendLine categories={spec.labels} series={spec.series.map((s) => ({ name: s.name, data: s.values }))} height={240} />;
    default: {
      const exhaustive: never = spec.kind;
      return exhaustive;
    }
  }
}

export function StudyChartGrid({ charts }: { charts: ChartSpec[] }) {
  if (charts.length === 0) return null;
  return (
    <div className="grid gap-4 md:grid-cols-2">
      {charts.map((c, i) => (
        <Card key={`${c.title}-${i}`} className="p-4">
          <p className="mb-2 text-sm font-semibold">{c.title}</p>
          <StudyChart spec={c} />
        </Card>
      ))}
    </div>
  );
}
